import React from 'react';
import { WithTranslation, withTranslation, useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components/macro';
import { TFunction } from 'i18next';

import { distances, session_states, transaction_states } from '../../../styles/constants';
import { DateTime, Money } from '../../../components/Formatters';
import { H2, H3, Muted } from '../../../components/Typography';
import { Session, SessionEvent, OrderAddress, ShippingOption } from '../../../types/checkout';
import { SYNTHETIC_SESSION_END_STATES } from '../../sessionHelpers';
import { formatPhoneNumber, formatCountryCode } from '../../../helpers/formatters';
import { CaptureStates } from '../actions';
import Hr from '../../../components/Hr';
export interface SessionHistoryProps extends WithTranslation {
    session: Session | undefined;
    sessionExpired: boolean;
    captureState: CaptureStates;
}

const AddressView = (
    {
        address,
    }: {
        address: OrderAddress;
    }
) => {
    const [t] = useTranslation();
    return (
        <>
            {address.phone_number && <AddressField>{formatPhoneNumber(address.phone_number)}</AddressField>}
            {address.email && <AddressField>{address.email}</AddressField>}
            {address.organization_number && (
                <AddressField>
                    <Muted>{t('payments.fields.organization_number')}</Muted> {address.organization_number}
                </AddressField>
            )}
            {address.co_address && (
                <AddressField>
                    <Muted>c/o</Muted> {address.co_address}
                </AddressField>
            )}
            {(address.first_name || address.last_name) && (
                <AddressField>
                    {address.first_name} {address.last_name}
                </AddressField>
            )}
            {address.business_name && <AddressField>{address.business_name}</AddressField>}
            {address.address_line && <AddressField>{address.address_line}</AddressField>}
            {address.address_line_2 && <AddressField>{address.address_line_2}</AddressField>}
            {(address.postal_code || address.postal_place) && (
                <AddressField>
                    {address.postal_code} {address.postal_place}
                </AddressField>
            )}
            {address.country && <AddressField>{formatCountryCode(address.country)}</AddressField>}
        </>
    );
};

const ShippingOptionView = (
    {
        shippingOption,
        session,
    }: {
        shippingOption: ShippingOption;
        session: Session;
    }
) => {
    const [t] = useTranslation();
    return (
        <>
            <ShippingOptionField>
                {t('payments.fields.shipping_option.id')}: {shippingOption.id}
            </ShippingOptionField>
            <ShippingOptionField>
                {t('payments.fields.shipping_option.operator')}: {shippingOption.operator}
            </ShippingOptionField>
            {shippingOption.operator_product_id && (
                <ShippingOptionField>
                    {t('payments.fields.shipping_option.operator_product_id')}: {shippingOption.operator_product_id}
                </ShippingOptionField>
            )}
            <ShippingOptionField>
                {' '}
                {t('payments.fields.shipping_option.title')}: {shippingOption.title}{' '}
            </ShippingOptionField>
            {shippingOption.delivery_method && (
                <ShippingOptionField>
                    {t('payments.fields.shipping_option.delivery_method')}:
                    {t(`payments.fields.shipping_option.delivery_methods.${shippingOption.delivery_method}` as any)}
                </ShippingOptionField>
            )}
            <ShippingOptionField>
                {t('payments.fields.shipping_option.amount')}:{' '}
                <Money amount={shippingOption.amount} currency={session.order.currency} />
            </ShippingOptionField>

            {shippingOption.pick_up_address && (
                <ShippingOptionField>
                    {t('payments.fields.shipping_option.pick_up_address')}:<br />
                    <Indented>
                        <AddressView address={shippingOption.pick_up_address} />
                    </Indented>
                </ShippingOptionField>
            )}
            {shippingOption.eta && (shippingOption.eta.starts_at || shippingOption.eta.ends_at) && (
                <ShippingOptionField>
                    {t('payments.fields.shipping_option.eta')}:{' '}
                    {shippingOption.eta.starts_at && <DateTime isoString={shippingOption.eta.starts_at} />}
                    {shippingOption.eta.starts_at && shippingOption.eta.ends_at && ' - '}
                    {shippingOption.eta.ends_at && <DateTime isoString={shippingOption.eta.ends_at} />}
                </ShippingOptionField>
            )}
            {shippingOption.time_slot && (shippingOption.time_slot.starts_at || shippingOption.time_slot.ends_at) && (
                <ShippingOptionField>
                    {t('payments.fields.shipping_option.time_slot')}:{' '}
                    {shippingOption.time_slot.starts_at && <DateTime isoString={shippingOption.time_slot.starts_at} />}
                    {shippingOption.time_slot.starts_at && shippingOption.time_slot.ends_at && ' - '}
                    {shippingOption.time_slot.ends_at && <DateTime isoString={shippingOption.time_slot.ends_at} />}
                </ShippingOptionField>
            )}
        </>
    );
};

const getStatusTitle = (t: TFunction, event: SessionEvent) => {
    return t([`payments.virtual_terminal.poll_session.events.${event.name}`, event.name || '']);
};

const getDotClassName = (current: boolean, event: SessionEvent) => {
    if (!current) {
        return '';
    }
    if (SYNTHETIC_SESSION_END_STATES.includes(event.name || '')) {
        return 'active';
    }
    return 'active pulsate';
};

const predefinedColors = [
    transaction_states.AUTHORIZED,
    transaction_states.FAILED,
    transaction_states.REFUNDED,
    transaction_states.CAPTURED,
    transaction_states.PARTIALLY_REFUNDED,
    transaction_states.INITIATED,
];

const newColor = (index: number): string => {
    return predefinedColors[index] || '#' + Math.floor(Math.random() * 2 ** 24)
        .toString(16)
        .padStart(6, '0');
};

const createInstanceColorizer = () => {
    const colorsByInstanceId: Record<string, string> = {};
    let index = 0;
    return (instanceId: string) => {
        if (!colorsByInstanceId[instanceId]) {
            colorsByInstanceId[instanceId] = newColor(index);
            index += 1;
        }
        return colorsByInstanceId[instanceId];
    };
};

const SessionHistory = (
    {
        t,
        session,
        sessionExpired,
        captureState,
    }: SessionHistoryProps
) => {
    let events: SessionEvent[] = [];
    if (session && session.events) {
        events = [...session.events];
    }
    if (session && sessionExpired) {
        events.push({
            name: 'EXPIRED',
            id: 'synthetic',
            created_at: session.expires_at,
        });
    }
    if (session && captureState === CaptureStates.Captured) {
        events.push({
            name: 'CAPTURED',
            id: 'synthetic',
            created_at: session.updated_at || '',
        });
    } else if (session && captureState === CaptureStates.Failed) {
        events.push({
            name: 'CAPTURE_FAILED',
            id: 'synthetic',
            created_at: session.updated_at || '',
        });
    }
    if (events.some((event) => event.name === 'PUSH_NOTIFICATION_SENT')) {
        events = events.filter((event) => event.name !== 'INITIATED').filter((event) => event.name !== 'VISITED');
    }

    events = events.map((event) => {
        if (event.name === 'VISITED' && event.details && event.details.payment_product_type) {
            event.name = 'PRELOADED';
        }
        return event;
    });
    const instanceColorizer = createInstanceColorizer();
    return (
        <Wrapper>
            <H2>{t('payments.virtual_terminal.poll_session.events_title')}</H2>
            <Hr />
            <div>
                {events.map((event, i) => (
                    <EventWrapper key={i}>
                        <Dot status={event.name || ''} className={getDotClassName(i === events.length - 1, event)} />
                        <H3>{getStatusTitle(t, event)} </H3>
                        {event.details && (event.details.payment_product_type || event.details.error) && (
                            <DetailsWrapper>
                                {event.details.payment_product_type &&
                                    t([
                                        `payments.product_types.${event.details.payment_product_type}`,
                                        event.details.payment_product_type,
                                    ])}
                                {event.details.error && (
                                    <>
                                        {': '}
                                        <em>{event.details.error}</em>
                                    </>
                                )}
                            </DetailsWrapper>
                        )}
                        {event.name === 'VISITED' && event.details && event.details.ui && (
                            <DetailsWrapper>
                                {t('payments.fields.ui')}: {event.details.ui}
                            </DetailsWrapper>
                        )}
                        {event.name === 'VISITED' && event.details && event.details.sdk && (
                            <DetailsWrapper>
                                {t('payments.fields.sdk')}: {event.details.sdk}
                            </DetailsWrapper>
                        )}
                        {event.name === 'VISITED' && event.details && event.details.role && (
                            <DetailsWrapper>
                                {t('payments.fields.role')}: {event.details.role}
                            </DetailsWrapper>
                        )}
                        {event.name === 'SET_SHIPPING_ADDRESS' && event.details && event.details.shipping_address && (
                            <ShippingWrapper>
                                <AddressView address={event.details.shipping_address} />
                            </ShippingWrapper>
                        )}
                        {event.name === 'SET_BILLING_ADDRESS' && event.details && event.details.billing_address && (
                            <ShippingWrapper>
                                <AddressView address={event.details.billing_address} />
                            </ShippingWrapper>
                        )}
                        {event.name === 'SET_SHIPPING_OPTION' &&
                            event.details &&
                            event.details.shipping_option &&
                            session && (
                            <ShippingWrapper>
                                <ShippingOptionView
                                    shippingOption={event.details.shipping_option}
                                    session={session}
                                />
                            </ShippingWrapper>
                        )}
                        {event.name === 'SET_MY_DINTERO' && event.details && event.details?.my_dintero?.actions?.some((action) => action === 'create_user') && (
                            <DetailsWrapper>
                                {t('payments.fields.my_dintero.create_user')}
                            </DetailsWrapper>
                        )}
                        {event.request_headers && (
                            <>
                                {([
                                    'dintero-system-name',
                                    'dintero-system-version',
                                    'dintero-system-plugin-name',
                                    'dintero-system-plugin-version',
                                    'dintero-checkout-instance-id',
                                ] as const).map((field) => {
                                    const value = event && event.request_headers && event.request_headers[field];
                                    if (!value) {
                                        return undefined;
                                    }
                                    return (
                                        <DetailsWrapper key={field}>
                                            {t(`request_headers.${field}`)}:
                                            {field === 'dintero-checkout-instance-id' ? (
                                                <ColorSpan color={instanceColorizer(value)}>•</ColorSpan>
                                            ) : ('')}{''}
                                            {value}
                                        </DetailsWrapper>
                                    );
                                })}
                            </>
                        )}
                        <DateTimeWrapper>
                            <DateTime isoString={event.created_at || ''} boldTime />
                        </DateTimeWrapper>
                    </EventWrapper>
                ))}
            </div>
        </Wrapper>
    );
};

export default withTranslation()(SessionHistory);

const Wrapper = styled.div`
    padding-top: ${distances.normal};
`;



const progress = keyframes`
    from {
        transform:  scale(0.5);
    }
    to {
        transform:  scale(1.3);

    }
`;

interface DotProps {
    status: string;
}

const Dot = styled.div<DotProps>`
    position: absolute;
    top: ${distances.tiny};
    left: -6px;
    height: ${distances.small};
    width: ${distances.small};
    border-radius: ${distances.small};
    background: rgba(242, 242, 242, 1);

    &.active:after {
        content: ' ';
        position: absolute;
        top: 4px;
        left: 4px;
        height: ${distances.tiny};
        width: ${distances.tiny};
        border-radius: ${distances.tiny};
        background: ${(props) => session_states[props.status] || 'transparent'};
    }
    &.pulsate:after {
        animation: ${progress} 1s linear infinite;
    }
`;

const EventWrapper = styled.div`
    padding-top: 0;
    padding-left: ${distances.small};
    padding-bottom: 22px;
    width: 100%;
    font-size: 11px;
    position: relative;
    color: #bdc2c5;

    &:before {
        content: ' ';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
    &:last-child {
        color: #46535e;
    }
    &:first-child:before {
        content: ' ';
        position: absolute;
        top: ${distances.small};
        left: 0;
        bottom: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
    &:first-child:last-child:before {
        display: none;
    }
    &:hover {
        color: #46535e;
    }
    &:last-child:before {
        content: ' ';
        position: absolute;
        top: 0;
        height: 10px;
        left: 0;
        width: 4px;
        background: rgba(242, 242, 242, 1);
    }
`;

const ShippingWrapper = styled.div`
    font-weight: 400;
    margin-bottom: ${distances.tiny};
`;
const AddressField = styled.div``;
const ShippingOptionField = styled.div``;
const Indented = styled.div`
    width: 100%;
    border: none;
    padding-left: ${distances.small};
`;

const DetailsWrapper = styled.div`
    font-weight: 400;
    line-height: ${distances.small};
    margin-bottom: ${distances.tiny};
`;

const DateTimeWrapper = styled.div`
    font-weight: 400;
    line-height: ${distances.small};
    font-feature-settings: 'tnum';
`;

interface ColorProps {
    color: string;
}

const ColorSpan = styled.span<ColorProps>`
    padding-left: ${distances.nano};
    padding-right: ${distances.nano};
    color: ${(props) => props.color};
`;
